import { useState } from "react";
import { ArrowUpRight, Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Section, Eyebrow, FadeIn } from "@/components/Section";
import { api } from "@/lib/api";
import useSEO from "@/hooks/useSEO";

const services = ["Meta Ads", "Google Ads", "SEO", "Full mix", "Not sure yet"];
const budgets = [
  "< €5k / month",
  "€5k – €15k / month",
  "€15k – €40k / month",
  "€40k+ / month",
];

export default function Contact() {
  useSEO({
    title: "Contact · Free growth audit",
    description:
      "Book a free 30-minute growth audit with Bright Solutions Europe. Warsaw HQ · serving brands across Europe in Meta Ads, Google Ads and SEO.",
    path: "/contact",
  });
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      toast.success("Thanks! We'll be in touch within one business day.");
      setForm({
        name: "",
        email: "",
        company: "",
        service: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please email us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Section className="pt-36">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <FadeIn>
              <Eyebrow>Contact</Eyebrow>
              <h1 className="font-display mt-5 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.92]">
                Let's build
                <br />
                your <span className="text-[#ccff00]">growth engine.</span>
              </h1>
              <p className="mt-6 text-zinc-400 leading-relaxed">
                Tell us about your brand and goals. A senior strategist will
                respond within one business day with honest first thoughts —
                not a sales pitch.
              </p>
            </FadeIn>

            <FadeIn delay={0.1} className="mt-10 space-y-5">
              <div className="flex items-start gap-3">
                <Mail
                  className="h-5 w-5 text-[#ccff00] mt-0.5"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Email
                  </p>
                  <a
                    href="mailto:hello@brightsolutions.pro"
                    data-testid="contact-email-link"
                    className="text-white hover:text-[#ccff00] transition"
                  >
                    hello@brightsolutions.pro
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin
                  className="h-5 w-5 text-[#ccff00] mt-0.5"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Office
                  </p>
                  <p className="text-white">
                    BRIGHT SOLUTIONS EUROPE sp. z o.o.
                  </p>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    ul. Hoża 86 / 410
                    <br />
                    00-682 Warszawa, Poland
                  </p>
                  <p className="font-mono text-xs text-zinc-500 mt-1">
                    REGON 7011162230
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>

          <div className="md:col-span-7">
            <FadeIn delay={0.05}>
              {success ? (
                <div
                  data-testid="contact-success"
                  className="border border-[#ccff00] bg-[#ccff00]/5 p-10 md:p-12 flex flex-col items-start gap-4"
                >
                  <CheckCircle2 className="h-10 w-10 text-[#ccff00]" />
                  <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
                    Message received.
                  </h2>
                  <p className="text-zinc-300 max-w-md">
                    A strategist will reply within one business day. In the
                    meantime, feel free to explore our case studies.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    data-testid="contact-send-another"
                    className="mt-2 inline-flex items-center gap-2 text-[#ccff00] hover:text-white transition group font-medium"
                  >
                    Send another message
                    <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  className="border border-white/10 bg-[#0f0f10] p-8 md:p-10 space-y-6"
                  noValidate
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field
                      label="Your name *"
                      testid="contact-input-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jan Kowalski"
                      required
                    />
                    <Field
                      label="Work email *"
                      testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="jan@company.com"
                      required
                    />
                  </div>

                  <Field
                    label="Company / website"
                    testid="contact-input-company"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="brand.com"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <SelectField
                      label="What do you need?"
                      testid="contact-input-service"
                      value={form.service}
                      onChange={update("service")}
                      options={services}
                    />
                    <SelectField
                      label="Monthly ad budget"
                      testid="contact-input-budget"
                      value={form.budget}
                      onChange={update("budget")}
                      options={budgets}
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                      Tell us about your goals *
                    </label>
                    <textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      rows={5}
                      placeholder="Where are you today, where do you want to be, and by when?"
                      required
                      className="w-full bg-black border border-white/10 focus:border-[#ccff00] focus:outline-none text-white placeholder:text-zinc-600 px-4 py-3 text-sm leading-relaxed resize-none transition"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={loading}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#ccff00] hover:bg-[#d4ff33] disabled:opacity-60 disabled:cursor-not-allowed text-black px-6 py-4 font-bold tracking-tight transition group"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send message
                        <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </>
                    )}
                  </button>

                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                    We reply within 1 business day · No spam, ever
                  </p>
                </form>
              )}
            </FadeIn>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder, type = "text", required }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full bg-black border border-white/10 focus:border-[#ccff00] focus:outline-none text-white placeholder:text-zinc-600 px-4 py-3 text-sm transition"
      />
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
        {label}
      </label>
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full bg-black border border-white/10 focus:border-[#ccff00] focus:outline-none text-white px-4 py-3 text-sm transition appearance-none cursor-pointer"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
